/**
 * The week plan of a workout-log instance (CONTEXT.md: Planlagt økt, Planlagt
 * uke, Planlagt dag; docs/adr/0025, docs/adr/0026). Every rule about what a
 * plan claims and what may still change lives here, so the card, the modal
 * and scoring cannot disagree.
 */
import { DAY_KEYS, type DayKey, todayDayKeyInRange } from "@/lib/days";
import type {
  PlannedWorkout,
  Workout,
  WorkoutLogConfig,
  WorkoutLogDayData,
  WorkoutLogWeekData,
} from "@/lib/schema";

export function dayIndex(dayKey: DayKey): number {
  return DAY_KEYS.indexOf(dayKey);
}

/**
 * Where today falls relative to the week starting `monday`: its index inside
 * it, -1 for any day before the week, 7 for any day after. Comparing a day's
 * index against this is "has that day come yet".
 */
export function todayIndexInWeek(monday: string, now: Date): number {
  const inRange = todayDayKeyInRange(monday, now);
  if (inRange) return dayIndex(inRange);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return today < new Date(`${monday}T00:00:00Z`).getTime() ? -1 : 7;
}

/** The day a save made now is attributed to, clamped into the week. */
export function saveDayFor(todayIndex: number): DayKey {
  return DAY_KEYS[Math.min(Math.max(todayIndex, 0), 6)];
}

export function isPlanningOn(config: WorkoutLogConfig): boolean {
  return config.planning !== undefined;
}

function weekDataOf(weekData: unknown): WorkoutLogWeekData {
  return (weekData ?? {}) as WorkoutLogWeekData;
}

/**
 * The day the plan was first saved, or null when the week is not a Planlagt
 * uke. A week whose snapshot has planning switched off is never one, whatever
 * its data says — applying the mal can switch it off (docs/adr/0026).
 */
export function plannedOn(
  config: WorkoutLogConfig,
  weekData: unknown,
): DayKey | null {
  if (!isPlanningOn(config)) return null;
  return weekDataOf(weekData).plannedOn ?? null;
}

export function plannedWorkouts(
  config: WorkoutLogConfig,
  weekData: unknown,
): PlannedWorkout[] {
  if (!isPlanningOn(config)) return [];
  return weekDataOf(weekData).plannedWorkouts ?? [];
}

/** A Planlagt dag: on or after the day the plan was first saved. */
export function isPlannedDay(
  config: WorkoutLogConfig,
  weekData: unknown,
  dayKey: DayKey,
): boolean {
  const on = plannedOn(config, weekData);
  return on !== null && dayIndex(dayKey) >= dayIndex(on);
}

function dayDataOf(dayData: unknown): WorkoutLogDayData {
  return (dayData ?? { workouts: [] }) as WorkoutLogDayData;
}

/** The plans some workout of the week was ticked from. */
export function keptPlanIds(
  days: Partial<Record<DayKey, unknown>>,
): Set<string> {
  const kept = new Set<string>();
  for (const k of DAY_KEYS) {
    for (const w of dayDataOf(days[k]).workouts) {
      if (w.plannedWorkoutId) kept.add(w.plannedWorkoutId);
    }
  }
  return kept;
}

/**
 * The plans still waiting on `dayKey`. A kept plan is locked to its day, so
 * the workout that keeps it is always on this same day.
 */
export function unkeptPlansForDay(
  config: WorkoutLogConfig,
  weekData: unknown,
  dayKey: DayKey,
  dayData: unknown,
): PlannedWorkout[] {
  const kept = keptPlanIds({ [dayKey]: dayData });
  return plannedWorkouts(config, weekData).filter(
    (p) => p.day === dayKey && !kept.has(p.id),
  );
}

/**
 * Ferdiglogget (CONTEXT.md). On a Planlagt dag: nothing planned is left
 * unkept, so a planned rest day is green the moment the plan is saved.
 * Elsewhere, as before planning existed: an økt, or a declared hviledag.
 */
export function isLoggingCompleted(
  config: WorkoutLogConfig,
  weekData: unknown,
  dayKey: DayKey,
  dayData: unknown,
): boolean {
  if (isPlannedDay(config, weekData, dayKey)) {
    return unkeptPlansForDay(config, weekData, dayKey, dayData).length === 0;
  }
  const data = dayDataOf(dayData);
  return data.workouts.length > 0 || data.restDay === true;
}

/** Hviledag is declared only where no plan covers the day. */
export function offersRestDay(
  config: WorkoutLogConfig,
  weekData: unknown,
  dayKey: DayKey,
): boolean {
  return !isPlannedDay(config, weekData, dayKey);
}

/**
 * The day the Planlegging points land on, or null when the week earned none:
 * not planned, or saved after the Planleggingsfrist.
 */
export function planningAwardDay(
  config: WorkoutLogConfig,
  weekData: unknown,
): DayKey | null {
  const on = plannedOn(config, weekData);
  if (on === null || !config.planning) return null;
  const { deadline } = config.planning;
  if (deadline !== null && dayIndex(on) > dayIndex(deadline)) return null;
  return on;
}

/** Ticking a plan: an ordinary workout that remembers it (docs/adr/0025). */
export function workoutFromPlan(plan: PlannedWorkout): Workout {
  return {
    id: crypto.randomUUID(),
    label: plan.label,
    plannedWorkoutId: plan.id,
  };
}

/** A plan may be ticked on its own day or afterwards, never ahead. */
export function canTick(dayKey: DayKey, todayIndex: number): boolean {
  return dayIndex(dayKey) <= todayIndex;
}

/**
 * Whether the modal may edit or delete a plan: unkept, on today or later.
 * A plan on a day already behind us is history.
 */
export function canEditPlan(
  plan: PlannedWorkout,
  kept: Set<string>,
  todayIndex: number,
): boolean {
  return !kept.has(plan.id) && dayIndex(plan.day) >= todayIndex;
}

/** New plans, and moved ones, land only on today or later. */
export function acceptsPlans(dayKey: DayKey, todayIndex: number): boolean {
  return dayIndex(dayKey) >= todayIndex;
}

/**
 * «Kopier forrige uke»: last week's plan, weekday for weekday, under fresh
 * ids, skipping the days already behind us. Never its log.
 */
export function copyPlan(
  previous: PlannedWorkout[],
  todayIndex: number,
): PlannedWorkout[] {
  return previous
    .filter((p) => acceptsPlans(p.day, todayIndex))
    .map((p) => ({ id: crypto.randomUUID(), label: p.label, day: p.day }));
}
